import { Button, DatePicker, Form, Modal, Space, message } from 'antd';
import { retrySupplierConfirmation } from 'api/coreapi/orders';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';

interface RetrySupplierConfirmationModalProps {
    orderNumber: number;
    onRetried?: () => void;
}

const RetrySupplierConfirmationModal = ({ orderNumber, onRetried }: RetrySupplierConfirmationModalProps) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            const response = await retrySupplierConfirmation(orderNumber, values.newQuoteDeadline.toISOString());
            const result = response.result;
            if (!result) {
                throw new Error('La API no devolvió el resultado del reintento.');
            }
            if (result.internalActionRemainsOpen) {
                message.warning('No se encontraron invitaciones para enviar; la acción permanece abierta.');
            } else {
                message.success(`Se enviaron ${result.invitationsSentCount} invitaciones.`);
            }
            setOpen(false);
            form.resetFields();
            onRetried?.();
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }
            message.error(error?.errors?.[0] ?? 'No fue posible reintentar la confirmación de proveedores.');
        } finally {
            setSubmitting(false);
        }
    };

    return <>
        <Button onClick={() => setOpen(true)}>Reintentar proveedores</Button>
        <Modal
            title={`Reintentar proveedores para la orden #${orderNumber}`}
            open={open}
            onOk={submit}
            confirmLoading={submitting}
            okText="Reintentar"
            onCancel={() => setOpen(false)}>
            <Space direction="vertical" style={{ width: '100%' }}>
                <span>Define una nueva fecha límite para confirmar disponibilidad.</span>
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="newQuoteDeadline"
                        label="Nueva fecha límite"
                        rules={[
                            { required: true, message: 'Selecciona una fecha límite.' },
                            {
                                validator: (_, value?: Dayjs) => !value || value.isAfter(dayjs())
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('La fecha límite debe estar en el futuro.')),
                            },
                        ]}>
                        <DatePicker
                            showTime={{ format: 'HH:mm' }}
                            format="YYYY-MM-DD HH:mm"
                            style={{ width: '100%' }}
                            disabledDate={current => current.endOf('day').isBefore(dayjs())} />
                    </Form.Item>
                </Form>
            </Space>
        </Modal>
    </>;
};

export default RetrySupplierConfirmationModal;
